import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { hideChat } from 'src/utils/crisp';

import { ASSETS_API } from 'src/config-global';
import { useAuthContext } from 'src/auth/hooks';

import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import { useSettingsContext } from 'src/components/settings';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Home',
    linkTo: '/',
  },
  {
    label: 'Account',
    linkTo: '/account',
  },
  // {
  //   label: 'Knowledge Base',
  //   linkTo: paths.knowledgeBase.root,
  // },
  // {
  //   label: 'Settings',
  //   linkTo: '/account',
  // },

];

// ----------------------------------------------------------------------

export default function AccountPopover () {
  const router = useRouter();
  const settings = useSettingsContext();
  const theme = useTheme();


  const { logout, user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  const popover = usePopover();

  const handleLogout = async () => {
    try {
      await logout();
      popover.onClose();
      router.replace(paths.auth.login);
    } catch (error) {
      enqueueSnackbar("Couldn't log out. Try again.", { variant: 'error' });
    }
  };

  const handleClickItem = (path) => {
    popover.onClose();
    router.push(path);
  };

  const handlePopoverOpen = (event) => {
    // Hide Crisp chat when account popover opens to prevent overlap
    hideChat();
    popover.onOpen(event);
  };

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={handlePopoverOpen}
        sx={{
          width: 40,
          height: 40,
          background: (themeX) => alpha(themeX.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (themeX) =>
              `linear-gradient(135deg, ${themeX.palette.primary.light} 0%, ${themeX.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={user?.photoURL || `${ASSETS_API}/assets/images/avatar/avatar_${25}.jpg`}
          alt={user?.firstName}
          sx={{
            width: 36,
            height: 36,
            border: (themeX) => `solid 2px ${themeX.palette.background.default}`,
          }}
        >
          {user?.firstName} {user?.lastName}
        </Avatar>
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0, ZIndex: 3 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.firstName} {user?.lastName}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
              {option.label}
            </MenuItem>
          ))}

          <MenuItem
            sx={{ color: theme.palette.mode === 'light' ? 'secondary.dark' : 'primary.main' }}
            onClick={() => {
              popover.onClose()
              settings.onToggle()
            }}>
            Customize app
          </MenuItem>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Log out
        </MenuItem>
      </CustomPopover>
    </>
  );
}
